import { Component, Output, EventEmitter, Input, ViewChild, ElementRef } from '@angular/core';

@Component({
    selector: 'mon-import-wizard-upload',
    templateUrl: './upload.component.html',
    styleUrls: ['./upload.component.scss'],
    standalone: false,
})
export class ImportWizardUploadComponent {
    @Input() loading = false;
    @Output() fileUploaded = new EventEmitter<File>();
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    dragOver = false;
    selectedFile: File | null = null;
    error: string | null = null;

    /**
     * Handle file drop
     */
    onDrop (event: DragEvent): void {
        event.preventDefault();
        this.dragOver = false;

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            this.handleFile(files[0]);
        }
    }

    /**
     * Handle drag over
     */
    onDragOver (event: DragEvent): void {
        event.preventDefault();
        this.dragOver = true;
    }

    /**
     * Handle drag leave
     */
    onDragLeave (event: DragEvent): void {
        event.preventDefault();
        this.dragOver = false;
    }

    /**
     * Handle file input change
     */
    onFileSelected (event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.handleFile(input.files[0]);
        }
    }

    /**
     * Process selected file
     */
    private handleFile (file: File): void {
        this.error = null;

        if (!file) {
            return;
        }

        if (!file.name.toLowerCase().endsWith('.json')) {
            this.error = 'Please select a JSON file';
            return;
        }

        const maxSize = 10 * 1024 * 1024;
        if (file.size > maxSize) {
            this.error = 'File size must be less than 10MB';
            return;
        }

        this.selectedFile = file;
        this.fileUploaded.emit(file);
    }

    /**
     * Trigger file input click
     */
    triggerFileInput (): void {
        this.fileInput.nativeElement.click();
    }

    /**
     * Clear selected file
     */
    clearFile (): void {
        this.selectedFile = null;
        this.error = null;

        // Clear file input
        if (this.fileInput) {
            this.fileInput.nativeElement.value = '';
        }
    }

    /**
     * Format file size for display
     */
    formatFileSize (bytes: number): string {
        if (bytes === 0) {
            return '0 Bytes';
        }

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }
}
